import { useState, useEffect, Fragment } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getReportBranchHeaders } from '../utils/branchStorage';

const baht = (n) => (Number(n) || 0).toLocaleString('th-TH', { maximumFractionDigits: 0 });
const num = (n) => (Number(n) || 0).toLocaleString('th-TH');

// ช่วงวันที่แบบกดปุ่มเดียว — คำนวณตามเวลาไทยเสมอ ไม่ใช่เวลาเครื่อง ไม่งั้นพนักงาน
// ที่เปิดจากเครื่องตั้งเวลาผิดจะเห็นยอดของคนละวันกับที่ระบบตัดรอบจริง
const bangkokNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));

// ช่องตาราง — index.css ใส่ padding 1.2rem กับ color เข้มมาให้ หน้านี้เป็นธีมมืดจึงทับเอง
const cellBase = { padding: '0.7rem 0.6rem' };

// index.css ทาพื้นไล่สีอ่อนให้ `table th` โดยตรง ซึ่งทับพื้นมืดที่ตั้งไว้ที่ <tr>
// ตารางธีมมืดจึงต้องทับพื้นที่ th เอง ไม่งั้นหัวตารางสีอ่อนจะจมหายไปบนพื้นสว่าง
const headCell = { ...cellBase, background: '#1e1e2e', color: '#d4d7e0', fontWeight: 700 };

const RANGES = [
  { key: 'today', label: 'วันนี้', days: 0 },
  { key: '7d', label: '7 วัน', days: 6 },
  { key: '30d', label: '30 วัน', days: 29 },
];

function Kpi({ label, value, unit, hint, accent }) {
  return (
    <div className="card" style={{ display: 'grid', gap: '0.35rem', alignContent: 'start' }}>
      <div style={{ fontSize: '0.9rem', color: '#d4d7e0', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '1.9rem', fontWeight: 700, color: accent || '#8b9dff', lineHeight: 1.1 }}>
        {value}
        {unit && <span style={{ fontSize: '0.9rem', fontWeight: 500, marginLeft: '0.3rem', color: '#c5c9d3' }}>{unit}</span>}
      </div>
      {hint && <div style={{ fontSize: '0.8rem', color: '#b0b4c0' }}>{hint}</div>}
    </div>
  );
}

export default function SalesByCategory({ apiUrl }) {
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set()); // หมวดที่กางดูเมนูอยู่ (key มี prefix 'paid:'/'buffet:' กันชนกัน)
  const [showBuffet, setShowBuffet] = useState(false); // กางดูจำนวนเมนูบุฟเฟ่ต์ที่ถูกกินหรือไม่

  const toggle = (name) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  useEffect(() => {
    load();
  }, [range]);

  const load = async () => {
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    try {
      const opt = RANGES.find(r => r.key === range) || RANGES[0];
      const end = bangkokNow();
      end.setHours(23, 59, 59, 999);
      const start = bangkokNow();
      start.setDate(start.getDate() - opt.days);
      start.setHours(0, 0, 0, 0);

      const token = localStorage.getItem('adminToken');
      const qs = `startDate=${start.toISOString()}&endDate=${end.toISOString()}`;
      const response = await fetchWithRetry(`${apiUrl}/api/transactions/by-category?${qs}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });

      if (!response.ok) throw new Error('โหลดข้อมูลไม่สำเร็จ');
      const result = await response.json();
      setData(result.data || result);
    } catch (err) {
      console.error('Failed to load sales by category:', err);
      setError(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  const buffetRows = data?.buffet?.breakdown ?? [];
  const maxPaidQty = data?.categories?.reduce((m, c) => Math.max(m, c.quantity), 0) || 0;
  const maxBuffetQty = data?.buffetConsumption?.reduce((m, c) => Math.max(m, c.quantity), 0) || 0;

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div className="page-header">
        <h2>📂 ยอดขายตามประเภท</h2>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {RANGES.map(r => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            style={{
              padding: '0.5rem 1.1rem',
              borderRadius: '8px',
              border: range === r.key ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.15)',
              background: range === r.key ? 'rgba(102,126,234,0.2)' : 'rgba(255,255,255,0.05)',
              color: range === r.key ? '#a5b4fc' : '#d4d7e0',
              fontWeight: range === r.key ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#b0b4c0' }}>⏳ กำลังโหลด...</div>
      )}

      {error && (
        <div className="card" style={{ color: '#ff8a8a', fontWeight: 600 }}>⚠️ {error}</div>
      )}

      {!loading && !error && data && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <Kpi label="ยอดขายรวม" value={baht(data.grandTotal)} unit="บาท"
                 hint={`${num(data.receipts)} บิล · ${num(data.heads)} คน`} accent="#4ade80" />
            <Kpi label="ค่าบุฟเฟ่ต์" value={baht(data.buffet?.revenue)} unit="บาท"
                 hint={`${data.buffet?.share ?? 0}% ของยอดรวม`} />
            <Kpi label="เมนูสั่งเพิ่ม" value={baht(data.menu?.revenue)} unit="บาท"
                 hint={`${data.menu?.share ?? 0}% ของยอดรวม`} accent="#fbbf24" />
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div
              onClick={() => setShowBuffet(v => !v)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            >
              <h3 style={{ margin: 0 }}>
                {showBuffet ? '▴' : '▾'} ดูจำนวนเมนูบุฟเฟ่ต์ที่ถูกกินเยอะสุด
              </h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#b0b4c0', marginTop: '0.4rem', marginBottom: showBuffet ? '1rem' : 0 }}>
              จำนวนที่ขาย ไม่ใช่ยอดขาย — รวมอยู่ในค่าบุฟเฟ่ต์แล้วทั้งหมด
            </p>

            {showBuffet && (
              (!data.buffetConsumption || data.buffetConsumption.length === 0) ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#b0b4c0' }}>
                  ยังไม่มีข้อมูลเมนูบุฟเฟ่ต์ในช่วงนี้
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="table-dark" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem', background: 'transparent' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1e1e2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <th style={headCell}>หมวดหมู่</th>
                        <th style={{ ...headCell, textAlign: 'right' }}>จำนวนชิ้น</th>
                        <th style={{ ...headCell, minWidth: '120px' }}>สัดส่วนที่กิน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.buffetConsumption ?? []).map((c, index) => {
                        const key = 'buffet:' + c.name;
                        const open = expanded.has(key);
                        const maxItemQty = c.items?.reduce((m, i) => Math.max(m, i.quantity), 0) || 0;
                        const rowBg = index % 2 === 0 ? 'rgba(30, 30, 46, 0.8)' : 'rgba(39, 41, 61, 0.8)';

                        return (
                          <Fragment key={c.name}>
                            <tr
                              onClick={() => toggle(key)}
                              style={{
                                backgroundColor: open ? 'rgba(102, 126, 234, 0.18)' : rowBg,
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                cursor: c.items?.length ? 'pointer' : 'default'
                              }}
                            >
                              <td style={{ ...cellBase, color: '#e2e8f0', fontWeight: 600 }}>
                                <span style={{
                                  display: 'inline-block', width: '1.1rem', color: '#8b9dff',
                                  transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s'
                                }}>
                                  {c.items?.length ? '▸' : ''}
                                </span>
                                {c.name}
                                {c.items?.length > 0 && (
                                  <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: '#8b9dff', fontWeight: 500 }}>
                                    {c.items.length} เมนู
                                  </span>
                                )}
                              </td>
                              <td style={{ ...cellBase, color: '#e2e8f0', textAlign: 'right' }}>{num(c.quantity)}</td>
                              <td style={cellBase}>
                                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '8px' }}>
                                  <div style={{
                                    width: `${maxBuffetQty > 0 ? (c.quantity / maxBuffetQty) * 100 : 0}%`,
                                    height: '100%',
                                    borderRadius: '4px',
                                    background: '#818cf8'
                                  }} />
                                </div>
                              </td>
                            </tr>

                            {open && c.items.map(item => (
                              <tr key={c.name + '/' + item.name} style={{
                                backgroundColor: 'rgba(15, 15, 25, 0.85)',
                                borderBottom: '1px solid rgba(255,255,255,0.04)'
                              }}>
                                <td style={{ ...cellBase, paddingLeft: '2.2rem', color: '#b0b4c0', fontSize: '0.88rem' }}>
                                  {item.name}
                                </td>
                                <td style={{ ...cellBase, color: '#b0b4c0', textAlign: 'right', fontSize: '0.88rem' }}>
                                  {num(item.quantity)}
                                </td>
                                <td style={cellBase}>
                                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '3px', height: '5px' }}>
                                    <div style={{
                                      width: `${maxItemQty > 0 ? (item.quantity / maxItemQty) * 100 : 0}%`,
                                      height: '100%',
                                      borderRadius: '3px',
                                      background: 'rgba(129, 140, 248, 0.6)'
                                    }} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>รายได้แยกตามรายการ</h3>
            <p style={{ fontSize: '0.85rem', color: '#b0b4c0', marginTop: 0, marginBottom: '1rem' }}>
              ค่าบุฟเฟ่ต์ (แยกตามประเภทคน + รีฟิลน้ำ) และเมนูที่แยกราคาจากบุฟเฟ่ต์ (เช่น เครื่องดื่ม, เพิ่มเตาอย่าง) ·{' '}
              <strong>คลิกที่หมวด</strong> เพื่อกางดูรายเมนู
            </p>

            {(buffetRows.length === 0 && (!data.categories || data.categories.length === 0)) ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#b0b4c0' }}>
                ไม่มีรายได้ในช่วงนี้
              </div>
            ) : (
              // table ที่ไม่กำหนดอะไรจะตกไปใช้ index.css ที่เป็นธีมสว่าง (background: white)
              // ทุก <tr> จึงต้องกำหนดพื้นมืดของตัวเอง — แพตเทิร์นเดียวกับ DailySummary.jsx และ TransactionHistory.jsx
              <div style={{ overflowX: 'auto' }}>
                <table className="table-dark" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem', background: 'transparent' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1e1e2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th style={headCell}>หมวดหมู่</th>
                      <th style={{ ...headCell, textAlign: 'right' }}>จำนวนชิ้น</th>
                      <th style={{ ...headCell, minWidth: '120px' }}>สัดส่วนที่กิน</th>
                      <th style={{ ...headCell, textAlign: 'right' }}>ยอดเงิน</th>
                      <th style={{ ...headCell, textAlign: 'right' }}>% ของยอด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buffetRows.map(r => (
                      <tr key={'buffet-rev:' + r.name} style={{
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        borderBottom: '1px solid rgba(255,255,255,0.05)'
                      }}>
                        <td style={{ ...cellBase, color: '#e2e8f0', fontWeight: 600 }}>
                          <span style={{ display: 'inline-block', width: '1.1rem' }} />
                          {r.name}
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: '#8b9dff', fontWeight: 500 }}>
                            ค่าบุฟเฟ่ต์
                          </span>
                        </td>
                        <td style={{ ...cellBase, color: '#e2e8f0', textAlign: 'right' }}>
                          {r.quantity > 0 ? `${num(r.quantity)} คน` : '—'}
                        </td>
                        <td style={cellBase} />
                        <td style={{ ...cellBase, textAlign: 'right', fontWeight: 700, color: '#4ade80' }}>
                          {baht(r.revenue)}
                        </td>
                        <td style={{ ...cellBase, textAlign: 'right', color: '#b0b4c0' }}>
                          {r.share ?? 0}%
                        </td>
                      </tr>
                    ))}
                    {(data.categories ?? []).map((c, index) => {
                      const key = 'paid:' + c.name;
                      const open = expanded.has(key);
                      // แถบของเมนูเทียบกันภายในหมวด ไม่ใช่เทียบข้ามหมวด — ไม่งั้นเมนูในหมวดเล็กจะเห็นแต่ขีดจิ๊ดเดียว
                      const maxItemQty = c.items?.reduce((m, i) => Math.max(m, i.quantity), 0) || 0;
                      const rowBg = index % 2 === 0 ? 'rgba(30, 30, 46, 0.8)' : 'rgba(39, 41, 61, 0.8)';

                      return (
                        <Fragment key={c.name}>
                          <tr
                            onClick={() => toggle(key)}
                            style={{
                              backgroundColor: open ? 'rgba(102, 126, 234, 0.18)' : rowBg,
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              cursor: c.items?.length ? 'pointer' : 'default'
                            }}
                          >
                            <td style={{ ...cellBase, color: '#e2e8f0', fontWeight: 600 }}>
                              <span style={{
                                display: 'inline-block', width: '1.1rem', color: '#8b9dff',
                                transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s'
                              }}>
                                {c.items?.length ? '▸' : ''}
                              </span>
                              {c.name}
                              {c.items?.length > 0 && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', color: '#8b9dff', fontWeight: 500 }}>
                                  {c.items.length} เมนู
                                </span>
                              )}
                            </td>
                            <td style={{ ...cellBase, color: '#e2e8f0', textAlign: 'right' }}>{num(c.quantity)}</td>
                            <td style={cellBase}>
                              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '8px' }}>
                                <div style={{
                                  width: `${maxPaidQty > 0 ? (c.quantity / maxPaidQty) * 100 : 0}%`,
                                  height: '100%',
                                  borderRadius: '4px',
                                  background: '#fbbf24'
                                }} />
                              </div>
                            </td>
                            <td style={{ ...cellBase, textAlign: 'right', fontWeight: 700, color: '#4ade80' }}>
                              {baht(c.revenue ?? 0)}
                            </td>
                            <td style={{ ...cellBase, textAlign: 'right', color: '#b0b4c0' }}>
                              {c.share ?? 0}%
                            </td>
                          </tr>

                          {open && c.items.map(item => (
                            <tr key={c.name + '/' + item.name} style={{
                              backgroundColor: 'rgba(15, 15, 25, 0.85)',
                              borderBottom: '1px solid rgba(255,255,255,0.04)'
                            }}>
                              <td style={{ ...cellBase, paddingLeft: '2.2rem', color: '#b0b4c0', fontSize: '0.88rem' }}>
                                {item.name}
                              </td>
                              <td style={{ ...cellBase, color: '#b0b4c0', textAlign: 'right', fontSize: '0.88rem' }}>
                                {num(item.quantity)}
                              </td>
                              <td style={cellBase}>
                                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '3px', height: '5px' }}>
                                  <div style={{
                                    width: `${maxItemQty > 0 ? (item.quantity / maxItemQty) * 100 : 0}%`,
                                    height: '100%',
                                    borderRadius: '3px',
                                    background: 'rgba(251, 191, 36, 0.7)'
                                  }} />
                                </div>
                              </td>
                              <td style={{ ...cellBase, textAlign: 'right', fontSize: '0.88rem', color: '#86efac' }}>
                                {baht(item.revenue ?? 0)}
                              </td>
                              <td style={{ ...cellBase, textAlign: 'right', color: '#7a8091', fontSize: '0.82rem' }}>
                                {c.quantity > 0 ? `${Math.round((item.quantity / c.quantity) * 100)}% ของหมวด` : '—'}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
